// Orquestrador do envio em blocos: fatia o arquivo, envia N em paralelo,
// retenta com backoff, renova a credencial e agrega o progresso.
//
// O cliente NÃO compõe o vídeo. Ele só estaciona blocos; quem chama
// `Put Block List`, valida e publica é o servidor (invariantes 2 e 3 de
// apps/trails/video_upload_service.py).

import type { SasGrant, UploadProtocol } from './types';
import { AbortedError, HttpError, NetworkError, xhrPut } from './xhrPut';

/** Só faz sentido insistir nestes. 400/409/413 são definitivos. */
const RETRYABLE_STATUS = new Set([408, 429, 500, 502, 503, 504]);
const MAX_RETRIES_PER_BLOCK = 5;
/** Renova a 75% do TTL: sobra folga para o bloco em voo terminar. */
const RENEW_AT_FRACTION = 0.75;

/**
 * `base64("000042")` — determinístico e do MESMO tamanho para todo índice
 * (exigência do Azure). É isto que torna retomar possível sem estado no
 * cliente: basta perguntar ao servidor quais índices já chegaram.
 */
export function blockId(index: number): string {
  return btoa(String(index).padStart(6, '0'));
}

function blockUrl(base: string, protocol: UploadProtocol, index: number): string {
  const sep = base.includes('?') ? '&' : '?';
  const id = encodeURIComponent(blockId(index));
  // `comp=block` é o que distingue Put Block de Put Blob. Sem ele o corpo
  // sobrescreveria o blob inteiro a cada bloco.
  return protocol === 'azure-block-blob'
    ? `${base}${sep}comp=block&blockid=${id}`
    : `${base}${sep}blockid=${id}`;
}

function backoffMs(attempt: number): number {
  const base = Math.min(30_000, 500 * 2 ** attempt);
  return base + Math.random() * 250; // jitter: evita os 4 workers em uníssono
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    function onAbort() {
      clearTimeout(timer);
      reject(new AbortedError());
    }
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * Guarda a credencial vigente e renova por DURAÇÃO, nunca comparando
 * `expires_at` com `Date.now()`: o relógio da máquina do usuário pode estar
 * errado e o SAS morreria cedo demais (ou tarde demais, o que é pior).
 * `performance.now()` é monotônico — não pula com ajuste de NTP.
 */
class SasHolder {
  private url: string;
  private renewAt: number;
  private inflight: Promise<void> | null = null;

  constructor(
    grant: SasGrant,
    private readonly renewFn: () => Promise<SasGrant>
  ) {
    this.url = grant.upload_url;
    this.renewAt = performance.now() + grant.sas_ttl_seconds * 1000 * RENEW_AT_FRACTION;
  }

  get current(): string {
    return this.url;
  }

  get stale(): boolean {
    return performance.now() >= this.renewAt;
  }

  /** Deduplicada: com 4 workers, os 4 percebem a expiração ao mesmo tempo e
   *  emitiriam 4 SAS — o backend loga abuso a partir de 24 emissões. */
  renew(): Promise<void> {
    if (this.inflight) return this.inflight;
    this.inflight = (async () => {
      try {
        const grant = await this.renewFn();
        this.url = grant.upload_url;
        this.renewAt = performance.now() + grant.sas_ttl_seconds * 1000 * RENEW_AT_FRACTION;
      } finally {
        this.inflight = null;
      }
    })();
    return this.inflight;
  }
}

export interface BlockUploadParams {
  file: File;
  protocol: UploadProtocol;
  blockSizeBytes: number;
  blockCount: number;
  maxConcurrency: number;
  grant: SasGrant;
  /** Índices que o servidor já recebeu — pulados (retomada). */
  stagedIndexes?: number[];
  renewSas: () => Promise<SasGrant>;
  /** Total de bytes já confirmados + parciais em voo. Pode regredir numa
   *  retentativa; quem exibe é que decide travar a barra. */
  onProgress?: (loadedBytes: number) => void;
  signal: AbortSignal;
}

export async function uploadBlocks({
  file,
  protocol,
  blockSizeBytes,
  blockCount,
  maxConcurrency,
  grant,
  stagedIndexes = [],
  renewSas,
  onProgress,
  signal,
}: BlockUploadParams): Promise<void> {
  const sizeOf = (index: number) =>
    Math.min(blockSizeBytes, file.size - index * blockSizeBytes);

  const staged = new Set(stagedIndexes);
  const pending: number[] = [];
  let completedBytes = 0;
  for (let i = 0; i < blockCount; i += 1) {
    if (staged.has(i)) completedBytes += sizeOf(i);
    else pending.push(i);
  }

  const partial = new Map<number, number>();
  const emit = () => {
    if (!onProgress) return;
    let sum = completedBytes;
    for (const v of partial.values()) sum += v;
    onProgress(sum);
  };
  emit(); // já nasce com o que foi retomado

  const sas = new SasHolder(grant, renewSas);

  // Aborto interno: quando UM bloco falha de vez, os outros workers precisam
  // parar. Sem isto o `Promise.all` rejeita mas os XHRs em voo seguem gastando
  // banda num envio que já está condenado.
  const internal = new AbortController();
  const stop = internal.signal;
  const relay = () => internal.abort();
  if (signal.aborted) internal.abort();
  else signal.addEventListener('abort', relay, { once: true });

  const uploadOne = async (index: number): Promise<void> => {
    const start = index * blockSizeBytes;
    const chunk = file.slice(start, Math.min(start + blockSizeBytes, file.size));
    let attempt = 0;

    for (;;) {
      if (stop.aborted) throw new AbortedError();
      if (sas.stale) await sas.renew();

      try {
        await xhrPut({
          url: blockUrl(sas.current, protocol, index),
          body: chunk,
          signal: stop,
          onProgress: (loaded) => {
            partial.set(index, loaded);
            emit();
          },
        });
        partial.delete(index);
        completedBytes += chunk.size;
        emit();
        return;
      } catch (err) {
        partial.delete(index); // o parcial deste bloco não vale mais
        emit();

        if (err instanceof AbortedError) throw err;

        if (err instanceof HttpError) {
          if (err.status === 403) {
            // O SAS pode ter expirado DURANTE este bloco: começou válido,
            // terminou vencido. Renovar revalida a permissão de quebra —
            // curator despromovido recebe 404 aqui e o envio para.
            await sas.renew();
          } else if (!RETRYABLE_STATUS.has(err.status)) {
            throw err;
          }
        } else if (!(err instanceof NetworkError)) {
          throw err;
        }

        attempt += 1;
        if (attempt > MAX_RETRIES_PER_BLOCK) throw err;
        await sleep(backoffMs(attempt), stop);
      }
    }
  };

  // Pool: `cursor++` é seguro porque JS é single-threaded — não há corrida
  // entre a leitura e o incremento.
  let cursor = 0;
  const worker = async (): Promise<void> => {
    for (;;) {
      const slot = cursor;
      cursor += 1;
      if (slot >= pending.length) return;
      try {
        await uploadOne(pending[slot]);
      } catch (err) {
        internal.abort(); // derruba os irmãos antes de propagar
        throw err;
      }
    }
  };

  try {
    // `Promise.all` rejeita com a PRIMEIRA rejeição em ordem de tempo — que é
    // a falha real, já que é ela que dispara o aborto das outras.
    await Promise.all(
      Array.from(
        { length: Math.max(1, Math.min(maxConcurrency, pending.length || 1)) },
        () => worker()
      )
    );
  } finally {
    signal.removeEventListener('abort', relay);
  }
}
